import {
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { toE164, type SignUpValues } from '@sf/shared';
import { sendWithContinueUrl } from '../../lib/authLinks';
import { auth, db } from '../../lib/firebase';

export async function signUp(values: SignUpValues): Promise<void> {
  const cred = await createUserWithEmailAndPassword(auth, values.email, values.password);
  try {
    await updateProfile(cred.user, { displayName: values.name });
    await setDoc(doc(db, 'users', cred.user.uid), {
      name: values.name,
      phone: toE164(values.countryCode, values.phone),
      email: values.email,
      unitNumber: values.unitNumber,
      role: 'tenant',
      status: 'pending',
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    // Don't leave an auth account without a profile behind.
    await cred.user.delete().catch(() => {});
    throw err;
  }
  // Welcome + verify email (D34). Best-effort: the account is created either way, and the
  // tenant can resend it from the awaiting-approval screen.
  await sendWithContinueUrl((s) => sendEmailVerification(cred.user, s)).catch(() => {});
}

export async function login(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(auth, email, password);
}

export async function logout(): Promise<void> {
  await signOut(auth);
}

export async function resetPassword(email: string): Promise<void> {
  try {
    await sendWithContinueUrl((s) => sendPasswordResetEmail(auth, email, s));
  } catch (err) {
    // Don't reveal whether an account exists.
    if ((err as { code?: string }).code !== 'auth/user-not-found') throw err;
  }
}

/** Sends the welcome + verify email again to the signed-in user. */
export async function resendVerification(): Promise<void> {
  if (!auth.currentUser) throw Object.assign(new Error('Signed out'), { code: 'auth/no-user' });
  const user = auth.currentUser;
  await sendWithContinueUrl((s) => sendEmailVerification(user, s));
}

/** Applies the code from a verify-email link. Refreshes the signed-in user if there is one. */
export async function confirmEmail(oobCode: string): Promise<void> {
  await applyActionCode(auth, oobCode);
  await auth.currentUser?.reload().catch(() => {});
}

/** Checks a reset link and returns the account's email. */
export async function checkResetCode(oobCode: string): Promise<string> {
  return verifyPasswordResetCode(auth, oobCode);
}

export async function setNewPassword(oobCode: string, password: string): Promise<void> {
  await confirmPasswordReset(auth, oobCode, password);
}
